import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { OptimizedImage } from './OptimizedImage';

const Footer: React.FC = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <footer className="bg-gray-100 text-gray-600 pt-8 pb-4 border-t border-gray-200 relative min-h-[320px]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative min-h-[280px]">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {/* Company Info - spans 2 columns on mobile, 2 on md, 1 on lg */}
          <div className="col-span-2 md:col-span-2 lg:col-span-1">
            <OptimizedImage
              src="/ticket-hauk-footer.png"
              alt="Ticket Hauk"
              className="h-10 w-auto mb-3 object-contain"
              width={160}
              height={40}
              priority
            />
            <p className="text-xs text-gray-500">
              Premium tickets at unbeatable prices. Experience entertainment like never before.
            </p>
            <div className="flex space-x-3 mt-3">
              <a href="#" className="text-gray-500 hover:text-aurora-teal transition-colors">
                <Facebook className="h-4 w-4" />
              </a>
              <a href="#" className="text-gray-500 hover:text-aurora-teal transition-colors">
                <Twitter className="h-4 w-4" />
              </a>
              <a href="#" className="text-gray-500 hover:text-aurora-teal transition-colors">
                <Instagram className="h-4 w-4" />
              </a>
              <a href="#" className="text-gray-500 hover:text-aurora-cyan transition-colors">
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-1">
            <h3 className="text-gray-900 font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {isAuthenticated ? (
                <>
                  <li><Link to="/dashboard" className="text-sm hover:text-hauk-cyan-600 transition-colors">Dashboard</Link></li>
                  <li><Link to="/search" className="text-sm hover:text-hauk-cyan-600 transition-colors">Search Events</Link></li>
                  <li><Link to="/alerts" className="text-sm hover:text-hauk-cyan-600 transition-colors">My Alerts</Link></li>
                  <li><Link to="/profile" className="text-sm hover:text-hauk-cyan-600 transition-colors">Profile</Link></li>
                  <li><Link to="/api-access" className="text-sm hover:text-hauk-cyan-600 transition-colors">API Access</Link></li>
                </>
              ) : (
                <>
                  <li><Link to="/features" className="text-sm hover:text-hauk-cyan-600 transition-colors">Features</Link></li>
                  <li><Link to="/how-it-works" className="text-sm hover:text-hauk-cyan-600 transition-colors">How It Works</Link></li>
                  <li><Link to="/pricing" className="text-sm hover:text-hauk-cyan-600 transition-colors">Pricing</Link></li>
                  <li><Link to="/login" className="text-sm hover:text-hauk-cyan-600 transition-colors">Sign In</Link></li>
                  <li><Link to="/register" className="text-sm hover:text-hauk-cyan-600 transition-colors">Sign Up</Link></li>
                </>
              )}
            </ul>
          </div>

          {/* Events */}
          <div className="md:col-span-1">
            <h3 className="text-gray-900 font-semibold mb-4">Browse Events</h3>
            <ul className="space-y-2">
              <li><Link to="/search" className="text-sm hover:text-hauk-cyan-600 transition-colors">All Events</Link></li>
              <li><Link to="/search?category=sports" className="text-sm hover:text-hauk-cyan-600 transition-colors">Sports</Link></li>
              <li><Link to="/search?category=music" className="text-sm hover:text-hauk-cyan-600 transition-colors">Concerts</Link></li>
              <li><Link to="/search?category=theater" className="text-sm hover:text-hauk-cyan-600 transition-colors">Theater</Link></li>
              <li><Link to="/search?category=comedy" className="text-sm hover:text-hauk-cyan-600 transition-colors">Comedy</Link></li>
              <li><Link to="/search?category=family" className="text-sm hover:text-hauk-cyan-600 transition-colors">Family</Link></li>
              <li><Link to="/search?category=arts" className="text-sm hover:text-hauk-cyan-600 transition-colors">Arts & Culture</Link></li>
            </ul>
          </div>

          {/* Contact Info - positioned in second row, second column on mobile */}
          <div className="md:col-span-1">
            <h3 className="text-gray-900 font-semibold mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-center">
                <Phone className="h-4 w-4 mr-2 text-hauk-cyan-600" />
                <span className="text-sm">1-800-HAUK-TIX</span>
              </li>
              <li className="flex items-center">
                <Mail className="h-4 w-4 mr-2 text-hauk-cyan-600" />
                <span className="text-sm">tickethauk@gmail.com</span>
              </li>
              <li className="flex items-start">
                <MapPin className="h-4 w-4 mr-2 text-hauk-cyan-600 mt-1" />
                <span className="text-sm">
                  123 N Michigan Ave<br />
                  Chicago, IL 60601
                </span>
              </li>
            </ul>
          </div>

          {/* Support & Legal - positioned in second row, first column on mobile */}
          <div className="md:col-span-1">
            <h3 className="text-gray-900 font-semibold mb-4">Support & Legal</h3>
            <ul className="space-y-2">
              <li><Link to="/terms" className="text-sm hover:text-hauk-cyan-600 transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-sm hover:text-hauk-cyan-600 transition-colors">Privacy Policy</Link></li>
              {isAuthenticated && (
                <>
                  <li><Link to="/api-access" className="text-sm hover:text-hauk-cyan-600 transition-colors">API Documentation</Link></li>
                  <li><a href="mailto:tickethauk@gmail.com" className="text-sm hover:text-hauk-cyan-600 transition-colors">Email Support</a></li>
                </>
              )}
              {!isAuthenticated && (
                <>
                  <li><Link to="/api-access" className="text-sm hover:text-hauk-cyan-600 transition-colors">Request API Access</Link></li>
                  <li><a href="mailto:tickethauk@gmail.com" className="text-sm hover:text-hauk-cyan-600 transition-colors">Contact Sales</a></li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              © 2024 Ticket Hauk. All rights reserved.
            </p>
            <div className="flex space-x-4 mt-4 md:mt-0 items-center">
              {/* Payment method images - using placeholder service for now */}
              <img 
                src="https://img.shields.io/badge/Visa-1A1F71?style=for-the-badge&logo=visa&logoColor=white" 
                alt="Visa" 
                className="h-8"
              />
              <img 
                src="https://img.shields.io/badge/Mastercard-EB001B?style=for-the-badge&logo=mastercard&logoColor=white" 
                alt="Mastercard" 
                className="h-8"
              />
              <img 
                src="https://img.shields.io/badge/Amex-006FCF?style=for-the-badge&logo=americanexpress&logoColor=white" 
                alt="American Express" 
                className="h-8"
              />
              <img 
                src="https://img.shields.io/badge/PayPal-00457C?style=for-the-badge&logo=paypal&logoColor=white" 
                alt="PayPal" 
                className="h-8"
              />
              <img 
                src="https://img.shields.io/badge/Apple%20Pay-000000?style=for-the-badge&logo=applepay&logoColor=white" 
                alt="Apple Pay" 
                className="h-8"
              />
              <img 
                src="https://img.shields.io/badge/Google%20Pay-4285F4?style=for-the-badge&logo=googlepay&logoColor=white" 
                alt="Google Pay" 
                className="h-8"
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
